<template>
  <div class="w-100">
    <!-- Added tiers: full width table -->
    <div class="tw-overflow-x-auto" v-if="tiers.length">
      <b-table small :items="tiers" :fields="tableFields">
        <template #cell(min_amt)="data">
          {{ data.value }}
        </template>
        <template #cell(img)="data">
          <b-img :src="data.value" height="60" />
        </template>
        <template #cell(actions)="{ index }">
          <div class="d-flex gap-2 tw-items-center justify-content-end">
            <b-icon-caret-up-fill
              v-if="index !== 0"
              variant="danger"
              role="button"
              font-scale="1.2"
              @click="move(index, -1)"
            />
            <b-icon-caret-down-fill
              v-if="index !== tiers.length - 1"
              variant="danger"
              role="button"
              font-scale="1.2"
              @click="move(index, 1)"
            />
            <b-icon-pencil-fill
              variant="danger"
              role="button"
              font-scale="1.2"
              @click="edit(index)"
            />
            <b-icon-trash-fill
              variant="danger"
              role="button"
              font-scale="1.2"
              @click="remove(index)"
            />
          </div>
        </template>
      </b-table>
    </div>

    <!-- Add / edit form: two columns -->
    <b-row>
      <b-col cols="12" md="6">
        <b-form-group
          class="form-group-custom w-100"
          :label="$t('edit_campaign.donor_card_img_by_amt_min_amt', 'Minimum amount')"
          :description="
            $t(
              'edit_campaign.donor_card_img_by_amt_min_amt_description',
              'The image applies to donations greater than this amount (in the campaign currency).'
            )
          "
        >
          <b-form-input v-model.number="item.min_amt" type="number" min="0" />
        </b-form-group>
      </b-col>
      <b-col cols="12" md="6">
        <b-form-group
          class="form-group-custom w-100"
          :label="$t('edit_campaign.donor_card_img_by_amt_img', 'Card background image')"
          :description="
            $t(
              'edit_campaign.donor_card_img_by_amt_img_description',
              'Upload an image (crops to 444x155) or paste an image URL.'
            )
          "
        >
          <b-form-file
            v-model="fileInput"
            accept="image/*"
            :placeholder="
              $t(
                'edit_campaign.donor_card_img_by_amt_choose_file',
                'Choose a file or drop it here...'
              )
            "
            :drop-placeholder="
              $t('edit_campaign.donor_card_img_by_amt_drop_file', 'Drop file here...')
            "
            @input="onFileChange"
          />
          <b-input-group class="mt-2">
            <b-form-input
              v-model="item.img"
              :placeholder="
                $t('edit_campaign.donor_card_img_by_amt_enter_url', 'Enter image url...')
              "
            />
            <template #append>
              <div v-if="uploading" class="d-flex align-items-center px-2">
                <b-spinner small />
              </div>
              <div v-else-if="item.img" class="btn h-100" role="button" @click="clearImg">
                <b-icon-arrow-clockwise rotate="45" scale="1.5" />
              </div>
            </template>
          </b-input-group>
          <b-img v-if="item.img && !uploading" :src="item.img" height="60" class="mt-2" />
        </b-form-group>
      </b-col>
    </b-row>

    <b-button
      type="button"
      variant="outline-dark"
      block
      :disabled="uploading || !item.img"
      @click="addOrUpdate"
    >
      {{
        editingIndex !== null
          ? $t("edit_campaign.setting_form_list_edit", "Edit")
          : $t("edit_campaign.setting_form_list_add", "Add")
      }}
    </b-button>

    <MediaStorageCropperModal campaignTemplate="donor_card_img_by_amt" ref="cropper" />
  </div>
</template>

<script>
import axios from "axios";
import urls from "@/constants/urls";
import { notificationsMixin } from "@/mixins";
import MediaStorageCropperModal from "@/views/media-storage/MediaStorageCropperModal.vue";

const CARD_ASPECT_RATIO = { value: 444 / 155, text: "444/155" };

export default {
  name: "DonorCardTiers",
  components: {
    MediaStorageCropperModal,
  },
  mixins: [notificationsMixin],
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    field: {
      type: Object,
      default: () => null,
    },
    fieldKey: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      // Local reactive copy. The `value` prop comes from form-generetor's setDefault
      // via direct assignment, so it is NOT deeply observed and in-place mutations do
      // not refresh <b-table> (it caches by array reference). We keep a local copy and
      // assign a fresh array on every change, then emit the new array up for persistence.
      draft: [],
      item: { min_amt: 0, img: "" },
      fileInput: null,
      editingIndex: null,
      uploading: false,
    };
  },
  computed: {
    tiers() {
      return this.draft;
    },
    tableFields() {
      return [
        {
          key: "min_amt",
          label: this.$t("edit_campaign.donor_card_img_by_amt_min_amt", "Minimum amount"),
        },
        {
          key: "img",
          label: this.$t("edit_campaign.donor_card_img_by_amt_img", "Card background image"),
        },
        {
          key: "actions",
          label: this.$t("edit_campaign.donor_card_img_by_amt_actions", "Actions"),
          tdClass: "align-middle text-right",
          thClass: "text-right",
        },
      ];
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(v) {
        this.draft = Array.isArray(v) ? v.slice() : [];
      },
    },
  },
  methods: {
    commit(tiers) {
      this.draft = tiers;
      this.$emit("input", tiers);
    },
    getMediaUrl(file) {
      return new Promise((resolve, reject) => {
        if (file instanceof File || file instanceof Blob) {
          const formData = new FormData();
          formData.append("file", file);
          axios
            .post(urls.loadImageQuill, formData)
            .then((response) => resolve(response.data.src))
            .catch(reject);
        } else {
          resolve(file);
        }
      });
    },
    onFileChange(file) {
      if (!(file instanceof File)) {
        return;
      }
      this.$refs.cropper.open({
        file,
        tag: "donor_card_img_by_amt",
        aspectRatio: CARD_ASPECT_RATIO,
        allowAnyAspectRatio: true,
      });
      this.$refs.cropper.onOK(async (blob) => {
        this.uploading = true;
        try {
          this.item.img = await this.getMediaUrl(blob);
        } catch (e) {
          this.$_notificationsMixin_handleCatch(e);
        } finally {
          this.uploading = false;
        }
      });
      this.$refs.cropper.onClose(() => {});
      this.$nextTick(() => {
        this.fileInput = null;
      });
    },
    addOrUpdate() {
      if (!this.item.img) {
        return;
      }
      const entry = {
        min_amt: Number(this.item.min_amt) || 0,
        img: this.item.img,
      };
      const tiers = this.draft.slice();
      if (this.editingIndex !== null) {
        tiers[this.editingIndex] = entry;
      } else {
        tiers.push(entry);
      }
      this.commit(tiers);
      this.reset();
    },
    edit(index) {
      this.editingIndex = index;
      this.item = { ...this.draft[index] };
    },
    remove(index) {
      const tiers = this.draft.slice();
      tiers.splice(index, 1);
      this.commit(tiers);
      if (this.editingIndex === index) {
        this.reset();
      }
    },
    move(index, direction) {
      const target = index + direction;
      if (target < 0 || target > this.draft.length - 1) {
        return;
      }
      const tiers = this.draft.slice();
      [tiers[index], tiers[target]] = [tiers[target], tiers[index]];
      this.commit(tiers);
    },
    clearImg() {
      this.item.img = "";
      this.fileInput = null;
    },
    reset() {
      this.item = { min_amt: 0, img: "" };
      this.fileInput = null;
      this.editingIndex = null;
    },
  },
};
</script>
